import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  ModalContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useErrorHandler,
  Switch,
  Label,
} from '@sandtech-hub-monorepo/ui-kit';
import { ReactComponent as CertificateIcon } from '../../assets/Icons/certificate-2-icon.svg';
import React from 'react';
import { z } from 'zod';
import { X } from 'lucide-react';
import { BulbIcon } from '@sandtech-hub-monorepo/ui-kit-site';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@apollo/client';
import {
  UpdateUserDocument,
  UpdateSkillsDocument,
  EmploymentTypeEnum,
} from '@sandtech-hub-monorepo/ehub-client';
import { Loader2Icon } from 'lucide-react';
import { LevelOfStudySuggestions } from '@sandtech-hub-monorepo/ui-kit-site';

// Soft skills options for multi-select
const SoftSkillsOptions = [
  'Communication',
  'Teamwork',
  'Problem Solving',
  'Critical Thinking',
  'Adaptability',
  'Time Management',
  'Leadership',
  'Creativity',
  'Work Ethic',
  'Attention to Detail',
  'Emotional Intelligence',
  'Conflict Resolution',
];

const formSchema = z.object({
  highestLevelOfStudy: z
    .string()
    .min(1, 'Please select your highest level of education'),
  fieldOfStudy: z.string().min(1, 'Please enter your field of study'),
  isEmployed: z.boolean(),
  employmentType: z.nativeEnum(EmploymentTypeEnum).optional(),
  openToNewPositions: z.boolean(),
  topCareerGoal: z.string().min(1, 'Please enter your top career goal'),
  previouslyEnrolledInALX: z.boolean(),
  linkedIn: z.string().min(1, 'Please enter your LinkedIn profile'),
  skills: z.array(z.string()).refine(
    (skills) => {
      const softSkillsCount = skills.filter((skill) =>
        SoftSkillsOptions.includes(skill)
      ).length;
      return softSkillsCount === 3;
    },
    { message: 'Please select exactly 3 soft skills from the list' }
  ),
});

interface Props {
  onComplete: () => void;
  defaultValues?: {
    highestLevelOfStudy?: string | null;
    fieldOfStudy?: string | null;
    isEmployed?: boolean | null;
    employmentType?: EmploymentTypeEnum | null;
    openToNewPositions?: boolean | null;
    topCareerGoal?: string | null;
    previouslyEnrolledInALX?: boolean | null;
    linkedIn?: string | null;
    skills?: string[] | null;
  };
  allInfoCompleted?: boolean;
}

export const PersonalInformationModal = ({
  onComplete,
  defaultValues,
  allInfoCompleted = false,
}: Props) => {
  const { handleApolloError } = useErrorHandler();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      highestLevelOfStudy: defaultValues?.highestLevelOfStudy || '',
      fieldOfStudy: defaultValues?.fieldOfStudy || '',
      isEmployed: defaultValues?.isEmployed || false,
      employmentType: defaultValues?.employmentType || undefined,
      openToNewPositions: defaultValues?.openToNewPositions || false,
      topCareerGoal: defaultValues?.topCareerGoal || '',
      previouslyEnrolledInALX: defaultValues?.previouslyEnrolledInALX || false,
      linkedIn: defaultValues?.linkedIn || '',
      skills: defaultValues?.skills || [],
    },
  });

  const [updateUser, { loading: updatingUser }] = useMutation(
    UpdateUserDocument,
    {
      onError: handleApolloError,
    }
  );

  const [updateSkills, { loading: updatingSkills }] = useMutation(
    UpdateSkillsDocument,
    {
      onError: handleApolloError,
    }
  );

  const loading = updatingUser || updatingSkills;

  const submit = async () => {
    const values = form.getValues();
    const userInput = {
      highestLevelOfStudy: values.highestLevelOfStudy,
      fieldOfStudy: values.fieldOfStudy,
      isEmployed: values.isEmployed,
      employmentType: values.isEmployed ? values.employmentType : undefined,
      openToNewPositions: values.openToNewPositions,
      topCareerGoal: values.topCareerGoal,
      previouslyEnrolledInALX: values.previouslyEnrolledInALX,
      linkedInURL: values.linkedIn,
    };

    // Update user information (without skills)
    await updateUser({
      variables: { input: userInput },
    });

    // Update skills separately
    await updateSkills({
      variables: {
        input: {
          skills: values.skills,
        },
      },
    });

    // Navigate to next step (diversity modal)
    onComplete();
  };

  return (
    <ModalContent>
      <div className="flex flex-col items-center text-center gap-2 max-w-screen-xs mx-auto">
        <div className="p-6 bg-primary/20 rounded-full text-primary mx-auto mb-2">
          <CertificateIcon height="48" width="48" />
        </div>
        <h2 className="text-primary font-semibold sm:text-2xl text-xl">
          Personal Information
        </h2>
      </div>
      <div className="flex flex-col gap-6 max-w-screen-xs mx-auto p-3">
        <div className="bg-primary/10 p-2.5 rounded-md flex gap-3 items-center">
          <div className="p-2 text-primary">
            <BulbIcon />
          </div>
          <div className="text-primary text-sm">
            These details help us understand your background and match you with
            the right opportunities.
          </div>
        </div>
        <Form {...form}>
          <div className="flex flex-col gap-3">
            <FormField
              control={form.control}
              name="highestLevelOfStudy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">
                    Highest level of education
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger ref={field.ref}>
                        <SelectValue placeholder="Please select" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {LevelOfStudySuggestions.map((level) => (
                        <SelectItem value={level} key={level}>
                          {level}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="fieldOfStudy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">Field of study</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter your field of study" />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isEmployed"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">
                    Current employment status
                  </FormLabel>
                  <div className="flex items-center gap-3">
                    <Label className="text-sm font-normal">
                      {field.value ? 'Employed' : 'Unemployed'}
                    </Label>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </div>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            {form.watch('isEmployed') && (
              <FormField
                control={form.control}
                name="employmentType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm">Employment type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger ref={field.ref}>
                          <SelectValue placeholder="Please select" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.keys(EmploymentTypeEnum).map((key) => (
                          <SelectItem
                            value={
                              EmploymentTypeEnum[
                                key as keyof typeof EmploymentTypeEnum
                              ]
                            }
                            key={key}
                          >
                            {key}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="openToNewPositions"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-3">
                    <Label className="text-sm">
                      Are you looking for a job?
                    </Label>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <span className="text-sm">
                      {field.value ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="topCareerGoal"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">Top career goal</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter your top career goal"
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="previouslyEnrolledInALX"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-3">
                    <Label className="text-sm">
                      Have you enrolled in an ALX course before?
                    </Label>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                    <span className="text-sm">
                      {field.value ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="linkedIn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">LinkedIn profile</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      startAdornment="https://linkedin.com/in/"
                      placeholder="yourprofile"
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="skills"
              render={({ field }) => {
                // Filter to only count soft skills from our predefined list
                const selectedSoftSkills = (field.value || []).filter((skill) =>
                  SoftSkillsOptions.includes(skill)
                );
                const softSkillsCount = selectedSoftSkills.length;

                // Get available skills (not yet selected)
                const availableSkills = SoftSkillsOptions.filter(
                  (skill) => !selectedSoftSkills.includes(skill)
                );

                const addSkill = (skill: string) => {
                  if (
                    softSkillsCount < 3 &&
                    !selectedSoftSkills.includes(skill)
                  ) {
                    field.onChange([...(field.value || []), skill]);
                  }
                };

                const removeSkill = (skillToRemove: string) => {
                  field.onChange(
                    (field.value || []).filter((s) => s !== skillToRemove)
                  );
                };

                return (
                  <FormItem>
                    <FormLabel className="text-sm">
                      Select your top 3 soft skills
                    </FormLabel>
                    {softSkillsCount < 3 && (
                      <Select onValueChange={addSkill} value="">
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a skill" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableSkills.map((skill) => (
                            <SelectItem key={skill} value={skill}>
                              {skill}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <div
                      className={`flex flex-wrap gap-2 ${
                        selectedSoftSkills.length !== 0 && 'mt-3'
                      }`}
                    >
                      {selectedSoftSkills.map((skill) => (
                        <span
                          key={skill}
                          className="transition-all border inline-flex h-8 items-center text-sm pl-3 rounded-full bg-card hover:bg-muted text-foreground font-semibold"
                        >
                          {skill}
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => removeSkill(skill)}
                            className="py-1 px-3 h-full hover:bg-transparent hover:text-foreground rounded-full"
                          >
                            <X size={14} />
                          </Button>
                        </span>
                      ))}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {softSkillsCount} of 3 selected
                    </div>
                    <FormMessage className="text-xs" />
                  </FormItem>
                );
              }}
            />
          </div>
        </Form>
      </div>
      <Button
        className="mx-auto my-8"
        onClick={submit}
        disabled={loading || !form.formState.isValid}
      >
        {loading ? (
          <>
            <Loader2Icon className="animate-spin" />{' '}
            {allInfoCompleted ? 'Enrolling...' : 'Saving...'}
          </>
        ) : allInfoCompleted ? (
          'Confirm enrolment'
        ) : (
          'Next'
        )}
      </Button>
    </ModalContent>
  );
};
